import AssetReviewStepAccess from '../components/AssetReview/ReviewSteps/AssetReviewStepAccess.vue';
import AssetReviewStepAttributes from '../components/AssetReview/ReviewSteps/AssetReviewStepAttributes.vue';
import AssetReviewStepConfirm from '../components/AssetReview/ReviewSteps/AssetReviewStepConfirm.vue';
import AssetReviewStepDelete from '../components/AssetReview/ReviewSteps/AssetReviewStepDelete.vue';
import AssetReviewStepCustomQuestions from '../components/AssetReview/ReviewSteps/AssetReviewStepCustomQuestions.vue';
import AssetReviewStepDependencies from '../components/AssetReview/ReviewSteps/AssetReviewStepDependencies.vue';
import FunctionReview from '../components/AssetReview/ReviewSteps/FunctionReview.vue';

import SbBaseUrl from '../plugins/SbBaseUrl.js';
const baseUrl = SbBaseUrl.baseUrl;

export const reviewTypes = {
  keepDelete: {
    title: 'Retire/Keep',
    component: AssetReviewStepDelete,
    hideNextBtn: true,
  },
  custom: {
    title: 'Custom Questions',
    component: AssetReviewStepCustomQuestions,
  },
  access: { title: 'Access Review', component: AssetReviewStepAccess },
  functions: { title: 'Function Review', component: FunctionReview },
  attributes: {
    title: 'Attribute Review',
    component: AssetReviewStepAttributes,
  },
  serviceDependencies: {
    title: 'Windows Service',
    component: AssetReviewStepDependencies,
  },
  iisDependencies: {
    title: 'IIS Application Pool',
    component: AssetReviewStepDependencies,
  },
  taskDependencies: {
    title: 'Scheduled Task',
    component: AssetReviewStepDependencies,
  },
  confirmation: {
    title: 'Review Confirmation',
    component: AssetReviewStepConfirm,
  },
};

export const assetTypes = [
  {
    name: 'Servers',
    entityId: 1,
    detailsPageLink: (id) =>
      `${baseUrl}/Accounts#/ServerDetails?serverId=${id}`,
    reviewSteps: [reviewTypes.access],
  },
  {
    name: 'Groups',
    entityId: 11,
    detailsPageLink: (id) =>
      `${baseUrl}/ADGroup/IndividualGroupDetailsStsguid?stsGuid=${id}`,
    reviewSteps: [reviewTypes.attributes, reviewTypes.access],
  },
  {
    name: 'Applications',
    entityId: 2,
    detailsPageLink: (id) => `${baseUrl}/Accounts#/ApplicationDetails/${id}`,
    reviewSteps: [reviewTypes.access],
  },
  {
    name: 'Accounts',
    entityId: 3,
    detailsPageLink: (AccountId, Scope) =>
      `${baseUrl}/Accounts#/AccountDetails/${AccountId}?accountScope=${Scope}`,
    reviewSteps: [
      reviewTypes.attributes,
      reviewTypes.serviceDependencies,
      reviewTypes.iisDependencies,
      reviewTypes.taskDependencies,
    ],
  },
  {
    name: 'Collections',
    entityId: 7,
    detailsPageLink: (id) =>
      `${baseUrl}/UnstructuredData#/ShareDetails?FilersAndSharesID=${id}`,
    reviewSteps: [reviewTypes.access],
  },
  {
    name: 'Roles',
    entityId: 14,
    detailsPageLink: (id) => `${baseUrl}/Accounts#/applicationroles/${id}`,
    reviewSteps: [reviewTypes.functions, reviewTypes.access],
  },
  {
    name: 'Group Policies',
    entityId: 15,
    detailsPageLink: (id) => `${baseUrl}/GroupPolicy/ExportFile?id=${id}`,
    reviewSteps: [reviewTypes.attributes],
  },
  {
    name: 'Functions',
    entityId: 16,
    detailsPageLink: (id) => `${baseUrl}/Accounts#/function/${id}`,
    reviewSteps: [reviewTypes.access],
  },
].map((a) => {
  if (a.noDetailsPage) {
    // this link should never be shown to a user, but we need a method that
    // returns a valid link so we don't need to check for it everywhere
    a.detailsPageLink = (id) => `${baseUrl}/future-asset-details-page/${id}`;
  }
  a.reviewSteps = [
    reviewTypes.keepDelete,
    ...a.reviewSteps,
    reviewTypes.custom,
    reviewTypes.confirmation,
  ];
  return a;
});

export default assetTypes;

/**
 * Some aliases for asset names for backwards compatibility
 */
export const nameMap = {
  Server: 'Servers',
  Group: 'Groups',
  Application: 'Applications',
  Account: 'Accounts',
  Collection: 'Collections',
  AppRole: 'Roles',
  'Group Policy': 'Group Policies',
  Function: 'Functions',
  User: 'Users',
};

let _assetsWithNoDetailsPages = assetTypes
  .filter((a) => a.noDetailsPage)
  .map((a) => a.name);
Object.entries(nameMap).forEach(([k, v]) => {
  if (_assetsWithNoDetailsPages.includes(v)) {
    _assetsWithNoDetailsPages.push(k);
  }
});
export const assetsWithNoDetailsPages = _assetsWithNoDetailsPages;

const backwardsCompatibilityEntityIds = {
  Shares: 6,
  ServiceAccounts: 3,
};
export const ASSET_TYPE_ENUM = assetTypes.reduce((acc, curr) => {
  return {
    [curr.name]: curr.entityId,
    ...acc,
  };
}, backwardsCompatibilityEntityIds);

/**
 * Get asset type from the name, or a variation on the name
 * @param {string} name - The name key for the asset type
 * @note This method is meant to solve an issue where we're trying to get
 * an asset by the name, but we are sometimes using the singular, plural, or some
 * other variation on the name
 */
export const getAssetByName = (name) => {
  const validNames = assetTypes.map((a) => a.name);

  if (validNames.includes(name)) {
    return assetTypes.filter((a) => a.name === name)[0];
  } else if (Object.keys(nameMap).includes(name)) {
    return assetTypes.filter((a) => a.name === nameMap[name])[0];
  } else {
    throw `Invalid asset type name '${name}'`;
  }
};

/**
 * Get asset name from the value
 * @param {string} value - The name key for the asset type
 * @note This method is meant to solve an issue where we're trying to get
 * an asset by the name, but we are sometimes using the singular or plural
 */
export const getAssetNameByValue = (value) => {
  let valueMap = Object.entries(nameMap).reduce((ret, entry) => {
    const [key, value] = entry;
    ret[value] = key;
    return ret;
  }, {});

  if (Object.keys(valueMap).includes(value)) {
    return valueMap[value];
  }
  const standardNames = assetTypes.map((t) => t.name);
  if (standardNames.includes(value)) {
    return value;
  }
};

/**
 * Get Assets excluding array of item(s)(if any)
 * @param {[]} items - The assets to exclude
 * exclude assets from assetTypes array
 */
export const getAssetTypesExcludingItems = (items) => {
  return assetTypes.filter((i) => !items.includes(i.name));
};
